import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSupabaseAuth } from '../context/AuthContext';
import { isAllowedReturnPath } from '../lib/postLoginRedirect';
import { clearAuthRecoveryFlag } from '../lib/authToken';
import Wordmark from '../components/Wordmark';

function decodeAuth0ErrorDescription(raw) {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithRedirect, isLoading: auth0Initializing } = useAuth0();
  const { isAuthenticated, loading: authLoading, user } = useSupabaseAuth();
  const directSignin = searchParams.get('direct') === '1';
  const inviteEmail = useMemo(() => {
    const raw = searchParams.get('email')?.trim().toLowerCase() || '';
    return raw.includes('@') ? raw : '';
  }, [searchParams]);
  const [callbackError, setCallbackError] = useState('');
  const directLoginStarted = useRef(false);

  const validatedReturnTo = useMemo(() => {
    const raw = searchParams.get('next');
    if (!raw) return null;
    try {
      const decoded = decodeURIComponent(raw);
      return isAllowedReturnPath(decoded) ? decoded : null;
    } catch {
      return null;
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('mode') !== 'signup') return;
    const next = new URLSearchParams(searchParams);
    next.delete('mode');
    next.set('mode', 'signin');
    navigate({ pathname: '/auth', search: `?${next.toString()}` }, { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    const err = searchParams.get('error');
    const desc = searchParams.get('error_description');
    if (!err && !desc) return;

    const message = [err, decodeAuth0ErrorDescription(desc)].filter(Boolean).join(': ');
    setCallbackError(message);

    const next = new URLSearchParams();
    next.set('mode', 'signin');
    const direct = searchParams.get('direct');
    if (direct) next.set('direct', direct);
    const nextParam = searchParams.get('next');
    if (nextParam) next.set('next', nextParam);
    const emailParam = searchParams.get('email');
    if (emailParam) next.set('email', emailParam);
    const qs = next.toString();
    directLoginStarted.current = false;
    navigate({ pathname: '/auth', search: qs ? `?${qs}` : '' }, { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!directSignin) {
      directLoginStarted.current = false;
    }
  }, [directSignin]);

  const loginOpts = useCallback(() => {
    const appState = validatedReturnTo ? { returnTo: validatedReturnTo } : undefined;
    // Invite emails only ever go out to people who don't have an Auth0 account
    // yet (invite-user skips the email entirely for anyone already linked), so
    // the direct-from-email flow should send them to Auth0's signup screen —
    // hinting 'login' here made every first-time invitee land on a password
    // field for an account that doesn't exist yet. Manual visits to /auth
    // (no invite link) are real returning users, so those still hint 'login'.
    const authorizationParams = { screen_hint: directSignin ? 'signup' : 'login' };
    if (inviteEmail) authorizationParams.login_hint = inviteEmail;
    return {
      authorizationParams,
      ...(appState ? { appState } : {}),
    };
  }, [validatedReturnTo, inviteEmail, directSignin]);

  useEffect(() => {
    if (callbackError) return;
    if (!directSignin) return;
    if (auth0Initializing || authLoading) return;
    if (isAuthenticated) return;
    if (directLoginStarted.current) return;
    directLoginStarted.current = true;
    loginWithRedirect(loginOpts());
  }, [
    directSignin,
    callbackError,
    auth0Initializing,
    authLoading,
    isAuthenticated,
    loginWithRedirect,
    loginOpts,
  ]);

  useEffect(() => {
    if (callbackError) return;
    if (!isAuthenticated || auth0Initializing) return;
    clearAuthRecoveryFlag();
    navigate('/app', { replace: true });
  }, [isAuthenticated, auth0Initializing, callbackError, navigate]);

  function goSignIn() {
    loginWithRedirect(loginOpts());
  }

  const showDirectSigninLoader =
    directSignin && !callbackError && !isAuthenticated;

  if (showDirectSigninLoader) {
    return (
      <div className="auth-page">
        <div className="auth-shell">
          <div className="auth-head">
            <Wordmark />
            <button type="button" className="btn-ghost" onClick={() => navigate('/')}>Back to website</button>
          </div>
          <div className="auth-info" style={{ padding: '28px 8px', textAlign: 'center' }}>
            Signing you in with Auth0…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-head">
          <Wordmark />
          <button className="btn-ghost" type="button" onClick={() => navigate('/')}>Back to website</button>
        </div>

        <div className="auth-card">
          <h2>Sign in</h2>
          <p>
            Transformax is invitation-only during private beta. Use the work email from your invite to sign in with Auth0.
          </p>
          <div className="auth-trial-note">
            Need access? <button type="button" className="auth-inline-link" onClick={() => navigate('/#invite')}>Request an invitation</button>
          </div>

          {authLoading && <div className="auth-info">Checking your session...</div>}
          {callbackError && (
            <div className="form-error auth-error" role="alert">
              {callbackError}
              <div className="auth-info" style={{ marginTop: 8 }}>
                In Auth0 → Applications → your SPA → Settings, add this exact URL under{' '}
                <strong>Allowed Callback URLs</strong>:{' '}
                <code>{`${window.location.origin}/auth`}</code>
              </div>
            </div>
          )}
          {isAuthenticated && (
            <div className="auth-info">
              You are signed in{user?.email ? ` as ${user.email}` : ''}.
              <button type="button" className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => navigate('/app')}>
                Continue to app
              </button>
            </div>
          )}

          {!isAuthenticated && !authLoading && (
            <button
              type="button"
              className="btn-primary ob-full"
              style={{ marginTop: 16 }}
              onClick={goSignIn}
            >
              Continue to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
